import {
  ALL_MODULE_SYNERGY_DEFINITIONS,
  SYNERGY_GRADE_ORDER,
  type ModuleSynergyDefinition,
} from "../data/moduleSynergyDefinitions.js";

/**
 * **시너지 판정 resolver** (순수) — 현재 Start→Fire 활성 경로의 moduleId/slotType 목록을 받아
 * 발동 가능한 시너지를 계산하고 동시발동 상한으로 적용 대상을 제한한다. 도감 발견 후보(matchedSynergyIds)와
 * 전투 보정(matched effect 적용)이 같은 결과를 공유한다.
 *
 * 매칭: requiredModuleIds 전부 포함 ∧ (activeModuleGroups가 있으면 required가 인접 구간에 있음) ∧
 * forbiddenModuleIds 하나도 미포함 ∧ requiredSlotTypes 전부 포함 ∧
 * (minActiveModules>0 → activeModuleIds.length ≥ minActiveModules). 동시발동: main 1 + support 2 = 총 3.
 * 선정 정렬: priority desc → grade desc → id asc(안정).
 */
export interface SynergyResolveInput {
  readonly activeModuleIds: readonly string[];
  readonly activeSlotTypes: readonly string[];
  /**
   * Start→Fire 활성 경로 순서의 모듈별 definitionId 묶음.
   * 일반 모듈은 `[definitionId]`, 융합 모듈은 컴포넌트 definitionId 배열.
   * 지정되면 시너지는 requiredModuleIds가 서로 붙어 있는 경로 구간에서만 발동한다.
   */
  readonly activeModuleGroups?: readonly (readonly string[])[];
  /**
   * Start→Fire 활성 경로 순서의 모듈별 definitionId + 실제 점유 셀 묶음.
   * 지정되면 requiredModuleIds는 배열상 인접뿐 아니라 그리드 점유 셀이 상하좌우로 직접 맞닿아야 한다.
   */
  readonly activeModuleSlots?: readonly {
    readonly definitionIds: readonly string[];
    readonly cells: readonly { readonly x: number; readonly y: number }[];
  }[];
}

export interface SynergyResolveResult {
  /** 실제 적용/발견 대상 (≤3, main + supports를 정렬 순서로 합친 것). */
  readonly matched: readonly ModuleSynergyDefinition[];
  /** 최우선 시너지 (없으면 undefined). */
  readonly main?: ModuleSynergyDefinition;
  /** main 외 보조 시너지 (최대 2개). */
  readonly supports: readonly ModuleSynergyDefinition[];
}

/** 동시 발동 상한 — main 1 + support 2. */
const MAX_SUPPORTS = 2;
const MAX_TOTAL = 1 + MAX_SUPPORTS;

function hasTouchingCells(
  a: readonly { readonly x: number; readonly y: number }[],
  b: readonly { readonly x: number; readonly y: number }[],
): boolean {
  for (const ca of a) {
    for (const cb of b) {
      if (Math.abs(ca.x - cb.x) + Math.abs(ca.y - cb.y) === 1) return true;
    }
  }
  return false;
}

function hasAdjacentRequiredModulesInSlots(
  def: ModuleSynergyDefinition,
  activeModuleSlots: SynergyResolveInput["activeModuleSlots"],
): boolean {
  if (!activeModuleSlots || def.requiredModuleIds.length <= 1) return true;
  const required = new Set(def.requiredModuleIds);
  for (let start = 0; start < activeModuleSlots.length; start++) {
    const seen = new Set<string>();
    for (let end = start; end < activeModuleSlots.length; end++) {
      const slot = activeModuleSlots[end]!;
      if (end > start && !hasTouchingCells(activeModuleSlots[end - 1]!.cells, slot.cells)) break;
      const matchingIds = slot.definitionIds.filter((id) => required.has(id));
      if (matchingIds.length === 0) break;
      for (const id of matchingIds) seen.add(id);
      if (def.requiredModuleIds.every((id) => seen.has(id))) return true;
    }
  }
  return false;
}

function hasAdjacentRequiredModulesInGroups(
  def: ModuleSynergyDefinition,
  activeModuleGroups: readonly (readonly string[])[] | undefined,
): boolean {
  if (!activeModuleGroups || def.requiredModuleIds.length <= 1) return true;
  const required = new Set(def.requiredModuleIds);
  for (let start = 0; start < activeModuleGroups.length; start++) {
    const seen = new Set<string>();
    for (let end = start; end < activeModuleGroups.length; end++) {
      const matchingIds = activeModuleGroups[end]!.filter((id) => required.has(id));
      if (matchingIds.length === 0) break;
      for (const id of matchingIds) seen.add(id);
      if (def.requiredModuleIds.every((id) => seen.has(id))) return true;
    }
  }
  return false;
}

function matches(
  def: ModuleSynergyDefinition,
  moduleSet: ReadonlySet<string>,
  slotSet: ReadonlySet<string>,
  activeCount: number,
  activeModuleGroups: readonly (readonly string[])[] | undefined,
  activeModuleSlots: SynergyResolveInput["activeModuleSlots"],
): boolean {
  if (def.minActiveModules > 0 && activeCount < def.minActiveModules) return false;
  for (const need of def.requiredModuleIds) if (!moduleSet.has(need)) return false;
  if (activeModuleSlots) {
    if (!hasAdjacentRequiredModulesInSlots(def, activeModuleSlots)) return false;
  } else if (!hasAdjacentRequiredModulesInGroups(def, activeModuleGroups)) return false;
  for (const ban of def.forbiddenModuleIds) if (moduleSet.has(ban)) return false;
  for (const slot of def.requiredSlotTypes) if (!slotSet.has(slot)) return false;
  return true;
}

/** priority desc → grade desc → id asc 안정 정렬 비교자. */
function compareSelection(a: ModuleSynergyDefinition, b: ModuleSynergyDefinition): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  const ga = SYNERGY_GRADE_ORDER[a.grade];
  const gb = SYNERGY_GRADE_ORDER[b.grade];
  if (ga !== gb) return gb - ga;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function resolveModuleSynergies(
  input: SynergyResolveInput,
  defs: readonly ModuleSynergyDefinition[] = ALL_MODULE_SYNERGY_DEFINITIONS,
): SynergyResolveResult {
  const moduleSet = new Set(input.activeModuleIds);
  const slotSet = new Set(input.activeSlotTypes);
  const activeCount = input.activeModuleIds.length;

  const eligible = defs.filter((d) =>
    matches(d, moduleSet, slotSet, activeCount, input.activeModuleGroups, input.activeModuleSlots),
  );
  eligible.sort(compareSelection);

  const matched = eligible.slice(0, MAX_TOTAL);
  return {
    matched,
    main: matched[0],
    supports: matched.slice(1),
  };
}
